import { memo } from "react";
import { generatePath, Link } from "react-router";

import { cn } from "@/lib/chillnn/cn";
import type { HotelTopQuery } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { MEDIA_PAGE_URL } from "@/components/features/design/magazine/list/about/ui/components/magazine-media-publication-request";

import { useHotelFullUrls } from "@/components/features-new/hotel-top/components/_shared/use-hotel-full-urls";
import { LinkButton } from "@/components/features-new/hotel-top/components/island/hotel-urls/hotel-urls/components/link-button";
import { TestReservation } from "@/components/features-new/hotel-top/components/island/hotel-urls/test-reservation";

import { StepHeader } from "../_step-header";
import { TodoListSectionWrapper } from "../_todo-list-section-wrapper";
import { TodoListWrapper } from "../_todo-list-wrapper";

type Props = {
	hotelUrls: HotelTopQuery["hotelUrls"];
	hotelID: string;
	isTestReservationAvailable: boolean;
	disabled: boolean;
};

export const Step6 = memo(({ hotelUrls, hotelID, isTestReservationAvailable, disabled }: Props) => {
	const { fullUrlPlain, fullUrlMagazine, fullUrlSnack } = useHotelFullUrls(hotelUrls);

	return (
		<div className={cn("space-y-4", disabled && "pointer-events-none opacity-30")}>
			<StepHeader title="Step6" description="販売前最終チェック" />

			<TodoListWrapper>
				<TodoListSectionWrapper>
					<div className="space-y-6">
						<div className="space-y-1">
							<p className="text-base font-medium">販売チェック</p>
							<Link
								to={generatePath(PATHS.HOTEL.START_GUIDE_CHECKLIST, { hotelID })}
								target="_blank"
								rel="noopener noreferrer"
								className="block rounded p-2 text-base tracking-wide text-link-400 underline underline-offset-2 hover:bg-zinc-100"
							>
								販売前最終チェック表
							</Link>
						</div>

						<div className="space-y-4">
							<div className="space-y-1">
								<p className="text-base font-medium">テストURL</p>
								<p className="text-sm text-zinc-500">
									テスト予約を行い、実際の予約ページを確認してください
								</p>
							</div>

							<TestReservation
								hotelID={hotelID}
								isTestReservationAvailable={isTestReservationAvailable}
							/>
						</div>

						<div className="space-y-4">
							<p className="text-base font-medium">施設URL</p>

							<p className="text-sm text-zinc-500">公開される施設ページのURLです</p>

							<div className="space-y-3 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
								{fullUrlPlain && (
									<div>
										<p className="text-sm font-medium">【予約特化型】Plain / プレーン</p>
										<LinkButton url={fullUrlPlain} />
									</div>
								)}
								{fullUrlMagazine && (
									<div>
										<p className="text-sm font-medium">【ストーリー訴求型】Magazine / マガジン</p>
										<LinkButton url={fullUrlMagazine} />
									</div>
								)}
								{fullUrlSnack && (
									<div>
										<p className="text-sm font-medium">【ビジュアル訴求型】Snack / スナック</p>
										<LinkButton url={fullUrlSnack} />
									</div>
								)}
								{!fullUrlPlain && !fullUrlMagazine && !fullUrlSnack && (
									<p className="text-sm text-zinc-500">施設のURLはまだ設定されていません</p>
								)}
							</div>
						</div>

						<div className="space-y-4">
							<p className="text-base font-medium">ギャラリーサイト掲載依頼</p>

							<p className="text-sm text-zinc-500">
								<a
									href={MEDIA_PAGE_URL}
									target="_blank"
									rel="noopener noreferrer"
									className="text-link-400 underline underline-offset-2"
								>
									CHILLNNギャラリーサイト
								</a>
								への掲載をご希望の場合は、「マガジン設定画面」より申請いただけます。
								<br />
								※掲載の可否および掲載位置は、編集部による選考のうえ決定いたします。選考結果・理由・掲載時期については、個別のご連絡をいたしかねますので、あらかじめご了承ください。
							</p>

							<Link
								to={generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.HOTEL, { hotelID })}
								className="block rounded p-2 text-base tracking-wide text-link-400 underline underline-offset-2 hover:bg-zinc-100"
							>
								マガジンページ設定画面へ
							</Link>
						</div>
					</div>
				</TodoListSectionWrapper>
			</TodoListWrapper>
		</div>
	);
});

Step6.displayName = "Step6";
